import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Dropdown, Flex, MenuProps, Typography } from "antd";
import { LogoutOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { logoutUser } from "../../redux/Slices/AuthSlice";
import "./UserMenu.scss";

const avatarColors: React.CSSProperties = {
  backgroundColor: "var(--st-mint)",
  color: "var(--st-on-color)",
  border: "2px solid var(--st-outline)",
  fontWeight: 600,
};

/** Avatar and account menu, shared by the desktop Header and MobileNav. */
export const UserMenu: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const { t } = useTranslation("dashboard");
  // Kept here so the trigger can say whether its menu is open (antd's Dropdown adds no aria state).
  const [open, setOpen] = useState(false);

  const initials = (
    <>
      {user?.firstName?.charAt(0).toUpperCase()}
      {user?.lastName?.charAt(0).toUpperCase()}
    </>
  );

  const items: MenuProps["items"] = [
    {
      key: "user-info",
      label: (
        <Flex align="center" gap={12} style={{ padding: "4px 0" }}>
          <Avatar size={36} style={{ ...avatarColors, fontSize: 13, flexShrink: 0 }}>
            {initials}
          </Avatar>
          <div style={{ lineHeight: 1.4 }}>
            <Typography.Text strong style={{ display: "block", color: "var(--st-text)", fontSize: 13 }}>
              {user?.firstName} {user?.lastName}
            </Typography.Text>
            <Typography.Text style={{ fontSize: 12, color: "var(--st-text-muted)" }}>
              {user?.email}
            </Typography.Text>
          </div>
        </Flex>
      ),
      disabled: true,
      style: { cursor: "default", opacity: 1, height: "auto", padding: "8px 12px" },
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("userMenu.logout"),
      onClick: () => dispatch(logoutUser()),
      danger: true,
    },
  ];

  return (
    <Dropdown
      menu={{ items }}
      placement="bottomRight"
      trigger={["click"]}
      overlayStyle={{ minWidth: 240 }}
      open={open}
      onOpenChange={setOpen}
    >
      {/* A real button, so the keyboard reaches the menu; the avatar inside is its picture. */}
      <button
        type="button"
        className="st-user-menu__trigger"
        aria-label={t("userMenu.open")}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar size="large" className="st-user-menu__avatar" style={avatarColors}>
          {initials}
        </Avatar>
      </button>
    </Dropdown>
  );
};
